import { useEffect, useState } from "react";
import { adminApi } from "../api.js";
import styles from "../admin.module.css";

type DifficultyRule = { fameMax: 1 | 2 | 3; minYearGap: number };
type CrowflightDifficultyRule = {
  fameMax: 1 | 2 | 3;
  r1MinRatio: number;
  r2MinDegrees: number;
  r3MinRatio: number;
};

interface GameEntry {
  active?: boolean;
  sort?: number;
  nameDe?: string;
  nameEn?: string;
  taglineDe?: string;
  taglineEn?: string;
  rounds?: number;
  cardsPerRound?: number;
  // Difficulty rule shape differs per game (see DifficultyRule /
  // CrowflightDifficultyRule above); kept loose here since this admin form
  // just round-trips whatever the server already has.
  difficulty?: Record<"easy" | "normal" | "hard", Record<string, number>>;
  // Crowflight
  minPlacesForRegion?: number;
  r1MinAbsoluteKm?: number;
  // Huesswork
  gapWidthPercent?: Record<"easy" | "normal" | "hard", number>;
  hueDistance?: { min: number; max: number };
  minMidToEndDeltaE?: number;
}

type AllConfig = Record<string, GameEntry>;

const GAME_LABELS: Record<string, string> = {
  timeslip: "LineUp",
  crowflight: "Beeline",
  huesswork: "Halfway",
};

export function GameConfig() {
  const [config, setConfig] = useState<AllConfig | null>(null);
  const [savedGame, setSavedGame] = useState<string | null>(null);

  function reload() {
    adminApi.get<AllConfig>("/api/admin/game-config").then(setConfig);
  }

  useEffect(reload, []);

  function updateGame(gameId: string, patch: Partial<GameEntry>) {
    setConfig((c) => (c ? { ...c, [gameId]: { ...c[gameId], ...patch } } : c));
  }

  async function save(gameId: string) {
    if (!config) return;
    await adminApi.put(`/api/admin/game-config/${gameId}`, config[gameId]);
    setSavedGame(gameId);
    setTimeout(() => setSavedGame(null), 2000);
  }

  if (!config) return <p>Lädt …</p>;

  return (
    <div>
      <h1 className={styles.pageTitle}>Spiel-Konfiguration</h1>

      {Object.keys(GAME_LABELS).map((gameId) => {
        const entry = config[gameId] ?? {};
        return (
          <div key={gameId} className={styles.card}>
            <h2>{GAME_LABELS[gameId]}</h2>

            <div className={styles.checkboxRow}>
              <input
                id={`${gameId}-active`}
                type="checkbox"
                checked={Boolean(entry.active)}
                onChange={(e) => updateGame(gameId, { active: e.target.checked })}
              />
              <label htmlFor={`${gameId}-active`}>Aktiv (auf der Startseite sichtbar)</label>
            </div>

            <div className={styles.formRow}>
              <label htmlFor={`${gameId}-sort`}>Reihenfolge</label>
              <input
                id={`${gameId}-sort`}
                type="number"
                className={styles.input}
                value={entry.sort ?? 0}
                onChange={(e) => updateGame(gameId, { sort: Number(e.target.value) })}
              />
            </div>

            <div className={styles.formRow}>
              <label htmlFor={`${gameId}-name-de`}>Kachel-Name (DE, leer = Standard)</label>
              <input
                id={`${gameId}-name-de`}
                className={styles.input}
                value={entry.nameDe ?? ""}
                onChange={(e) => updateGame(gameId, { nameDe: e.target.value })}
              />
            </div>
            <div className={styles.formRow}>
              <label htmlFor={`${gameId}-name-en`}>Kachel-Name (EN, leer = Standard)</label>
              <input
                id={`${gameId}-name-en`}
                className={styles.input}
                value={entry.nameEn ?? ""}
                onChange={(e) => updateGame(gameId, { nameEn: e.target.value })}
              />
            </div>
            <div className={styles.formRow}>
              <label htmlFor={`${gameId}-tagline-de`}>Tagline (DE, leer = Standard)</label>
              <input
                id={`${gameId}-tagline-de`}
                className={styles.input}
                value={entry.taglineDe ?? ""}
                onChange={(e) => updateGame(gameId, { taglineDe: e.target.value })}
              />
            </div>
            <div className={styles.formRow}>
              <label htmlFor={`${gameId}-tagline-en`}>Tagline (EN, leer = Standard)</label>
              <input
                id={`${gameId}-tagline-en`}
                className={styles.input}
                value={entry.taglineEn ?? ""}
                onChange={(e) => updateGame(gameId, { taglineEn: e.target.value })}
              />
            </div>

            {gameId === "timeslip" && (
              <TimeslipSettings entry={entry} onChange={(patch) => updateGame(gameId, patch)} />
            )}
            {gameId === "crowflight" && (
              <CrowflightSettings entry={entry} onChange={(patch) => updateGame(gameId, patch)} />
            )}
            {gameId === "huesswork" && (
              <HuessworkSettings entry={entry} onChange={(patch) => updateGame(gameId, patch)} />
            )}

            <div className={styles.actions}>
              <button type="button" className={styles.button} onClick={() => save(gameId)}>
                Speichern
              </button>
              {savedGame === gameId && <span>Gespeichert.</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TimeslipSettings({
  entry,
  onChange,
}: {
  entry: GameEntry;
  onChange: (patch: Partial<GameEntry>) => void;
}) {
  const difficulty = entry.difficulty as Record<"easy" | "normal" | "hard", DifficultyRule> | undefined;

  function updateRule(level: "easy" | "normal" | "hard", patch: Partial<DifficultyRule>) {
    if (!difficulty) return;
    onChange({ difficulty: { ...difficulty, [level]: { ...difficulty[level], ...patch } } });
  }

  return (
    <>
      <div className={styles.formRow}>
        <label htmlFor="ts-rounds">Runden (3–10)</label>
        <input
          id="ts-rounds"
          type="number"
          min={3}
          max={10}
          className={styles.input}
          value={entry.rounds ?? 5}
          onChange={(e) => onChange({ rounds: Number(e.target.value) })}
        />
      </div>
      <div className={styles.formRow}>
        <label htmlFor="ts-cards">Karten pro Runde (4 oder 5)</label>
        <input
          id="ts-cards"
          type="number"
          min={4}
          max={5}
          className={styles.input}
          value={entry.cardsPerRound ?? 5}
          onChange={(e) => onChange({ cardsPerRound: Number(e.target.value) })}
        />
      </div>

      {difficulty && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Schwierigkeit</th>
              <th>Max. Bekanntheit</th>
              <th>Mindestabstand (Jahre)</th>
            </tr>
          </thead>
          <tbody>
            {(["easy", "normal", "hard"] as const).map((level) => (
              <tr key={level}>
                <td>{level === "easy" ? "Leicht" : level === "normal" ? "Normal" : "Schwer"}</td>
                <td>
                  <input
                    type="number"
                    min={1}
                    max={3}
                    className={styles.input}
                    value={difficulty[level].fameMax}
                    onChange={(e) =>
                      updateRule(level, { fameMax: Number(e.target.value) as 1 | 2 | 3 })
                    }
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min={1}
                    className={styles.input}
                    value={difficulty[level].minYearGap}
                    onChange={(e) => updateRule(level, { minYearGap: Number(e.target.value) })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function CrowflightSettings({
  entry,
  onChange,
}: {
  entry: GameEntry;
  onChange: (patch: Partial<GameEntry>) => void;
}) {
  const difficulty = entry.difficulty as
    | Record<"easy" | "normal" | "hard", CrowflightDifficultyRule>
    | undefined;

  function updateRule(level: "easy" | "normal" | "hard", patch: Partial<CrowflightDifficultyRule>) {
    if (!difficulty) return;
    onChange({ difficulty: { ...difficulty, [level]: { ...difficulty[level], ...patch } } });
  }

  return (
    <>
      <div className={styles.formRow}>
        <label htmlFor="cf-min-places">Mindestanzahl Orte pro Region</label>
        <input
          id="cf-min-places"
          type="number"
          min={1}
          className={styles.input}
          value={entry.minPlacesForRegion ?? 0}
          onChange={(e) => onChange({ minPlacesForRegion: Number(e.target.value) })}
        />
      </div>
      <div className={styles.formRow}>
        <label htmlFor="cf-r1-abs-km">R1 Mindestabstand absolut (km)</label>
        <input
          id="cf-r1-abs-km"
          type="number"
          min={0}
          className={styles.input}
          value={entry.r1MinAbsoluteKm ?? 0}
          onChange={(e) => onChange({ r1MinAbsoluteKm: Number(e.target.value) })}
        />
      </div>

      {difficulty && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Schwierigkeit</th>
              <th>Max. Bekanntheit</th>
              <th>R1 Mindestverhältnis</th>
              <th>R2 Mindestwinkel (°)</th>
              <th>R3 Mindestverhältnis</th>
            </tr>
          </thead>
          <tbody>
            {(["easy", "normal", "hard"] as const).map((level) => (
              <tr key={level}>
                <td>{level === "easy" ? "Leicht" : level === "normal" ? "Normal" : "Schwer"}</td>
                <td>
                  <input
                    type="number"
                    min={1}
                    max={3}
                    className={styles.input}
                    value={difficulty[level].fameMax}
                    onChange={(e) =>
                      updateRule(level, { fameMax: Number(e.target.value) as 1 | 2 | 3 })
                    }
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    max={1}
                    className={styles.input}
                    value={difficulty[level].r1MinRatio}
                    onChange={(e) => updateRule(level, { r1MinRatio: Number(e.target.value) })}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    min={0}
                    max={180}
                    className={styles.input}
                    value={difficulty[level].r2MinDegrees}
                    onChange={(e) => updateRule(level, { r2MinDegrees: Number(e.target.value) })}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    max={1}
                    className={styles.input}
                    value={difficulty[level].r3MinRatio}
                    onChange={(e) => updateRule(level, { r3MinRatio: Number(e.target.value) })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

function HuessworkSettings({
  entry,
  onChange,
}: {
  entry: GameEntry;
  onChange: (patch: Partial<GameEntry>) => void;
}) {
  const gapWidthPercent = entry.gapWidthPercent;
  const hueDistance = entry.hueDistance;

  function updateGapWidth(level: "easy" | "normal" | "hard", value: number) {
    if (!gapWidthPercent) return;
    onChange({ gapWidthPercent: { ...gapWidthPercent, [level]: value } });
  }

  return (
    <>
      <div className={styles.formRow}>
        <label htmlFor="hw-rounds">Runden (3–10)</label>
        <input
          id="hw-rounds"
          type="number"
          min={3}
          max={10}
          className={styles.input}
          value={entry.rounds ?? 5}
          onChange={(e) => onChange({ rounds: Number(e.target.value) })}
        />
      </div>

      {gapWidthPercent && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Schwierigkeit</th>
              <th>Boxen-Streuung (% der Gradientenbreite)</th>
            </tr>
          </thead>
          <tbody>
            {(["easy", "normal", "hard"] as const).map((level) => (
              <tr key={level}>
                <td>{level === "easy" ? "Leicht" : level === "normal" ? "Normal" : "Schwer"}</td>
                <td>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    className={styles.input}
                    value={gapWidthPercent[level]}
                    onChange={(e) => updateGapWidth(level, Number(e.target.value))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className={styles.formRow}>
        <label htmlFor="hw-hue-min">Farbtonabstand Endfarben – Minimum (°)</label>
        <input
          id="hw-hue-min"
          type="number"
          min={0}
          max={360}
          className={styles.input}
          value={hueDistance?.min ?? 0}
          onChange={(e) =>
            onChange({ hueDistance: { min: Number(e.target.value), max: hueDistance?.max ?? 0 } })
          }
        />
      </div>
      <div className={styles.formRow}>
        <label htmlFor="hw-hue-max">Farbtonabstand Endfarben – Maximum (°)</label>
        <input
          id="hw-hue-max"
          type="number"
          min={0}
          max={360}
          className={styles.input}
          value={hueDistance?.max ?? 0}
          onChange={(e) =>
            onChange({ hueDistance: { min: hueDistance?.min ?? 0, max: Number(e.target.value) } })
          }
        />
      </div>
      <div className={styles.formRow}>
        <label htmlFor="hw-min-delta-e">Mindest-ΔE der Mitte zu den Endfarben</label>
        <input
          id="hw-min-delta-e"
          type="number"
          min={0}
          className={styles.input}
          value={entry.minMidToEndDeltaE ?? 0}
          onChange={(e) => onChange({ minMidToEndDeltaE: Number(e.target.value) })}
        />
      </div>
    </>
  );
}
